import { Outlet } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideUserManagementScreen, questionAsked } from "../../state/slice";
// import useScreenSize from "../../hooks/useScreenSize";

import Footer from "../footer/footer.component";
import Header from "../header/header.component";
import EnhancedQuestionWrapper from "../question/enhanced-question-wrapper.component";
import EnhancedQuestion from "../question/enhanced-question.component";
import Answer from "../answer/answer.component";
import RecommendedQuestions from "../recommended-question/recommended-questions.component";
import ChatHistory from "../chat-history/chat-history.component";
import Feedback from "../feedback/feedback.component";
import CustomError from "../custom-error/custom-error-component";

import "./dashboard.component.scss";
import UserEntry from "../user/user-entry.component";
import useUser from "../../hooks/useUser";

/**
 * Dashboard Component - Main layout container for the "Ask Tim" application
 *
 * Responsible for:
 * - Managing the overall layout structure
 * - Orchestrating the question/answer flow
 * - Conditionally rendering components based on app state
 * - Managing responsive layout
 */
const Dashboard = () => {
  // Component state
  const [showQuestionInitial, setShowQuestionInitial] = useState(true);
  const [sidebarOpen, setSideBarOpen] = useState(false);

  // Hooks
  const dispatch = useDispatch();
  // const { isMobile } = useScreenSize();

  // Refs for DOM elements
  const mainContentRef = useRef<HTMLElement>(null);
  const bottomContainerRef = useRef<HTMLDivElement>(null);

  // Redux state selectors
  const isquestionAsked = useSelector((state: any) => state.question);
  const chatHistoryId = useSelector((state: any) => state.chatHistoryId);
  const showError = useSelector((state: any) => state.showError);
  const showRetryError = useSelector((state: any) => state.showRetryError);
  const isUserScreenAllowed = useUser();
  const showUserManagementScreen = useSelector(
    (state: any) => state.showUserManagementScreen
  );

  /**
   * Update bottom container width and position to match main content
   * Uses refs instead of direct DOM manipulation
   */
  const updateBottomContainerWidth = useCallback(() => {
    if (mainContentRef.current && bottomContainerRef.current) {
      const mainRect = mainContentRef.current.getBoundingClientRect();
      const containerWidth = Math.min(800, mainRect.width);

      bottomContainerRef.current.style.maxWidth = `${containerWidth}px`;
      bottomContainerRef.current.style.width = `${containerWidth}px`; // Explicitly set width to match
      bottomContainerRef.current.style.left = `${
        (window.innerWidth - containerWidth) / 2
      }px`;
      bottomContainerRef.current.style.right = "auto";

      // Apply the same width to the question card inside if it exists
      const questionCard =
        bottomContainerRef.current.querySelector(".question-card");
      if (questionCard) {
        (questionCard as HTMLElement).style.width = "100%";
        (questionCard as HTMLElement).style.maxWidth = `${containerWidth}px`;
      }
    }
  }, []);

  // Update state when question is asked
  useEffect(() => {
    if (isquestionAsked != null && isquestionAsked !== "") {
      scrollToTop();
      setShowQuestionInitial(false);
    }
  }, [isquestionAsked]);

  console.log("isshowretryerror on dashboard", showRetryError);

  // Scroll to top when chat history changes
  useEffect(() => {
    if (chatHistoryId > -1) {
      scrollToTop();
    }
  }, [chatHistoryId]);

  // Set up resize listener when bottom container is visible
  useEffect(() => {
    if (!showQuestionInitial) {
      // Use a slight delay to ensure components are fully rendered
      const timer = setTimeout(() => {
        updateBottomContainerWidth();
      }, 100);

      window.addEventListener("resize", updateBottomContainerWidth);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", updateBottomContainerWidth);
      };
    }
  }, [showQuestionInitial, updateBottomContainerWidth]);

  /**
   * Scroll to top of the page with smooth animation
   */
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /**
   * Open a new chat session
   * Resets question state and returns to initial question view
   */
  const openNewChat = () => {
    dispatch(questionAsked(""));
    dispatch(hideUserManagementScreen());
    setShowQuestionInitial(true);
  };

  /**
   * Toggle sidebar visibility
   */
  const handleViewSidebar = () => {
    setSideBarOpen(!sidebarOpen);
  };

  return (
    <div className="divMain">
      <Header />

      <main ref={mainContentRef} className="dashboard-main">
        <ChatHistory
          isOpen={sidebarOpen}
          toggleSidebar={handleViewSidebar}
          openNewChat={openNewChat}
          sidebarOpen={sidebarOpen}
        />

        {isUserScreenAllowed && showUserManagementScreen ? (
          <UserEntry />
        ) : showQuestionInitial ? (
          <EnhancedQuestionWrapper />
        ) : (
          <>
            <Answer question={isquestionAsked} />
            <RecommendedQuestions />
            <Feedback />
            <div ref={bottomContainerRef as React.RefObject<HTMLDivElement>}>
              <EnhancedQuestion type="bottom" />
            </div>
          </>
        )}
        {/* {showQuestionInitial ? (
          // Initial question view
          <EnhancedQuestionWrapper />
        ) : (
          // Answer view with related components
          <>
            <Answer question={isquestionAsked} />
            <RecommendedQuestions />
            <Feedback />
            <div ref={bottomContainerRef as React.RefObject<HTMLDivElement>}>
              <EnhancedQuestion type="bottom" />
            </div>
          </>
        )} */}


        {showError && <CustomError variant="big" msg="" />}
        {showRetryError && (
          <CustomError
            variant="small"
            msg="We're currently addressing a temporary issue. Your request is being retried — no action is needed."
          />
        )}

        <Outlet />
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
